import { useAssetType, usePriorityOptions } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { useFailureStatus } from "../../../hooks";

export interface FailureFilters {
  status: string;
  priority: string;
  assetType: string;
  search: string;
}

interface FailureFiltersProps {
  filters: FailureFilters;
  onFiltersChange: (filters: FailureFilters) => void;
  onApplyFilters: (filters: FailureFilters) => void;
  onClearFilters: () => void;
}

export const FailureFiltersComponent = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: FailureFiltersProps) => {
  // Hooks for filter options
  const { data: priorityList } = usePriorityOptions();
  const { data: statusList } = useFailureStatus();
  const { data: assetTypeList } = useAssetType();

  const createFormFields = (): FormField[] => {
    // Create options from hooks
    const priorityOptions =
      priorityList?.map((item: any) => ({
        value: String(item.key),
        label: item.value,
      })) || [];

    const statusOptions =
      statusList?.result?.map((item: any) => ({
        value: String(item.key),
        label: item.value,
      })) || [];

    const assetTypeOptions =
      assetTypeList?.result?.map((item: any) => ({
        value: String(item.code),
        label: item.name,
      })) || [];

    return [
      {
        name: "status",
        type: "select",
        label: "Status",
        required: false,
        options: [{ label: "All Statuses", value: "" }, ...statusOptions],
        classes: FIELD_CLASSES,
      },
      {
        name: "priority",
        type: "select",
        label: "Priority",
        required: false,
        options: [{ label: "All Priorities", value: "" }, ...priorityOptions],
        classes: FIELD_CLASSES,
      },
      {
        name: "assetType",
        type: "select",
        label: "Asset Type",
        required: false,
        options: [{ label: "All Asset Types", value: "" }, ...assetTypeOptions],
        classes: FIELD_CLASSES,
      },
    ];
  };

  // Create form instance
  const form: UseFormReturn<FailureFilters> =
    FormService.createForm<FailureFilters>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: FailureFilters) => {
    const currentValues = form.getValues();
    onApplyFilters({ ...currentValues, page: 1 });
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: FailureFilters = {
      status: "",
      priority: "",
      assetType: "",
      search: "",
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="flex justify-end items-center gap-2">
      <Button
        variant="outline"
        onClick={handleReset}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        <X className="h-4 w-4" />
        Clear Filters
      </Button>
      <Button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false, // We're using custom footer
          showResetButton: false, // We're using custom footer
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={3}
        responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 3 }}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
